/**
 * Client-side image → neon-tube tracer. Pure Canvas 2D, no dependencies.
 *
 * Pipeline: downsample → grayscale → box-blur → Otsu threshold → Moore-neighbor
 * contour trace → Ramer–Douglas–Peucker simplification → scene-space transform.
 * Good enough for logos and high-contrast art; noisy on photos by design.
 */

export interface TraceOptions {
  /** Longest-side ceiling for the working canvas. Default 512 px. */
  maxDimension: number;
  /**
   * Binarization cutoff (0–255). If null, Otsu's method picks one from the
   * grayscale histogram. Useful for dialing in anti-aliased edges.
   */
  threshold: number | null;
  /** Flip foreground/background — needed for dark-on-light source material. */
  invert: boolean;
  /** Box-blur radius in pixels before thresholding. Stabilizes anti-aliasing. */
  blurRadius: number;
  /** Drop contours shorter than this many pixels (in post-blur space). */
  minContourLength: number;
  /** Ramer–Douglas–Peucker tolerance, in pixels. Higher = fewer points. */
  simplifyEpsilon: number;
  /** Scene units for the longest axis of the output bbox. */
  targetWidth: number;
}

export const DEFAULT_TRACE_OPTIONS: TraceOptions = {
  maxDimension: 512,
  threshold: null,
  invert: false,
  blurRadius: 1,
  minContourLength: 24,
  simplifyEpsilon: 1.5,
  targetWidth: 4,
};

/** Pre-rendered previews for each stage of the pipeline. */
export interface TracePreviews {
  /** Downsampled source, no processing. */
  source: string;
  /** Binary mask as white-on-black — shows what threshold/invert produce. */
  mask: string;
  /** Source dimmed + cyan contour overlay — shows what got detected. */
  trace: string;
}

/** Raw data needed to re-render a color-dependent preview without retracing. */
export interface RenderBundle {
  width: number;
  height: number;
  pixelContours: Array<[number, number][]>;
}

export interface TraceResult {
  /** Tube-ready point arrays in scene space. One entry per contour. */
  contours: [number, number, number][][];
  /** Static previews of each pipeline stage. */
  previews: TracePreviews;
  /** Handle for rendering the color-dependent "neon" preview on demand. */
  render: RenderBundle;
  /** Binarization threshold that was actually used (post-Otsu). */
  usedThreshold: number;
  stats: { contourCount: number; totalPoints: number };
}

type Source = HTMLImageElement | ImageBitmap | HTMLCanvasElement;

export function traceImage(source: Source, opts: Partial<TraceOptions> = {}): TraceResult {
  const o: TraceOptions = { ...DEFAULT_TRACE_OPTIONS, ...opts };

  const { canvas, ctx } = drawToWorkingCanvas(source, o.maxDimension);
  const { width, height } = canvas;
  const imgData = ctx.getImageData(0, 0, width, height);

  const gray = toGrayscale(imgData);
  const blurred = o.blurRadius > 0 ? boxBlur(gray, width, height, o.blurRadius) : gray;

  const threshold = o.threshold ?? otsuThreshold(blurred);
  const mask = binarize(blurred, threshold, o.invert);

  const pixelContours = findContours(mask, width, height, o.minContourLength);
  const simplified = pixelContours.map((c) => simplify(c, o.simplifyEpsilon));

  const scene = toSceneSpace(simplified, width, height, o.targetWidth);
  const previews: TracePreviews = {
    source: canvas.toDataURL("image/png"),
    mask: renderMask(mask, width, height),
    trace: renderTracePreview(canvas, pixelContours),
  };

  return {
    contours: scene,
    previews,
    render: { width, height, pixelContours },
    usedThreshold: threshold,
    stats: {
      contourCount: scene.length,
      totalPoints: scene.reduce((n, c) => n + c.length, 0),
    },
  };
}

// ── Canvas & pixel helpers ──────────────────────────────────────────

function drawToWorkingCanvas(source: Source, maxDim: number) {
  const srcW = "width" in source ? source.width : (source as HTMLCanvasElement).width;
  const srcH = "height" in source ? source.height : (source as HTMLCanvasElement).height;
  const longest = Math.max(srcW, srcH);
  const scale = longest > maxDim ? maxDim / longest : 1;
  const width = Math.max(1, Math.round(srcW * scale));
  const height = Math.max(1, Math.round(srcH * scale));

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  if (!ctx) throw new Error("Could not get 2D context");
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(source as CanvasImageSource, 0, 0, width, height);
  return { canvas, ctx };
}

function toGrayscale(img: ImageData): Uint8ClampedArray {
  const out = new Uint8ClampedArray(img.width * img.height);
  const d = img.data;
  for (let i = 0, j = 0; i < d.length; i += 4, j++) {
    out[j] = (d[i] * 299 + d[i + 1] * 587 + d[i + 2] * 114) / 1000;
  }
  return out;
}

function boxBlur(src: Uint8ClampedArray, w: number, h: number, r: number): Uint8ClampedArray {
  const tmp = new Uint8ClampedArray(src.length);
  const out = new Uint8ClampedArray(src.length);
  const size = 2 * r + 1;

  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      let sum = 0;
      for (let k = -r; k <= r; k++) {
        const xi = Math.min(w - 1, Math.max(0, x + k));
        sum += src[y * w + xi];
      }
      tmp[y * w + x] = sum / size;
    }
  }
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      let sum = 0;
      for (let k = -r; k <= r; k++) {
        const yi = Math.min(h - 1, Math.max(0, y + k));
        sum += tmp[yi * w + x];
      }
      out[y * w + x] = sum / size;
    }
  }
  return out;
}

function otsuThreshold(gray: Uint8ClampedArray): number {
  const hist = new Array(256).fill(0);
  for (let i = 0; i < gray.length; i++) hist[gray[i]]++;
  const total = gray.length;

  let sumAll = 0;
  for (let t = 0; t < 256; t++) sumAll += t * hist[t];

  let sumBack = 0;
  let wBack = 0;
  let maxVar = -1;
  let threshold = 127;
  for (let t = 0; t < 256; t++) {
    wBack += hist[t];
    if (wBack === 0) continue;
    const wFore = total - wBack;
    if (wFore === 0) break;
    sumBack += t * hist[t];
    const mBack = sumBack / wBack;
    const mFore = (sumAll - sumBack) / wFore;
    const between = wBack * wFore * (mBack - mFore) * (mBack - mFore);
    if (between > maxVar) {
      maxVar = between;
      threshold = t;
    }
  }
  return threshold;
}

function binarize(gray: Uint8ClampedArray, threshold: number, invert: boolean): Uint8Array {
  const out = new Uint8Array(gray.length);
  for (let i = 0; i < gray.length; i++) {
    const on = gray[i] > threshold ? 1 : 0;
    out[i] = invert ? (on ? 0 : 1) : on;
  }
  return out;
}

// ── Moore-neighbor contour tracing ──────────────────────────────────

// Clockwise, starting from W (matches the standard Moore-neighbor convention).
const MOORE: ReadonlyArray<readonly [number, number]> = [
  [-1, 0], // 0: W
  [-1, -1], // 1: NW
  [0, -1], // 2: N
  [1, -1], // 3: NE
  [1, 0], // 4: E
  [1, 1], // 5: SE
  [0, 1], // 6: S
  [-1, 1], // 7: SW
];

function findContours(
  mask: Uint8Array,
  w: number,
  h: number,
  minLen: number,
): Array<[number, number][]> {
  const visited = new Uint8Array(mask.length);
  const contours: Array<[number, number][]> = [];

  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const idx = y * w + x;
      if (!mask[idx] || visited[idx]) continue;
      // Entry point: left neighbor is background (or out of bounds).
      const leftBg = x === 0 || !mask[idx - 1];
      if (!leftBg) continue;

      const contour = traceContour(mask, w, h, x, y, visited);
      if (contour.length >= minLen) contours.push(contour);
    }
  }
  return contours;
}

function traceContour(
  mask: Uint8Array,
  w: number,
  h: number,
  sx: number,
  sy: number,
  visited: Uint8Array,
): [number, number][] {
  const out: [number, number][] = [[sx, sy]];
  visited[sy * w + sx] = 1;

  let x = sx;
  let y = sy;
  // Entered from the west; start searching one step clockwise of that.
  let prevDir = 0;
  const maxSteps = 4 * (w + h);

  for (let step = 0; step < maxSteps; step++) {
    // Start search from "one after where we came from" — prevDir + 2 per
    // the radial-sweep rule (back up, then rotate CW).
    const startIdx = (prevDir + 2) % 8;
    let found = false;
    for (let i = 0; i < 8; i++) {
      const idx = (startIdx + i) % 8;
      const [dx, dy] = MOORE[idx];
      const nx = x + dx;
      const ny = y + dy;
      if (nx < 0 || ny < 0 || nx >= w || ny >= h) continue;
      if (mask[ny * w + nx]) {
        if (nx === sx && ny === sy) return out;
        x = nx;
        y = ny;
        prevDir = (idx + 4) % 8; // direction we came FROM, for next iter
        visited[ny * w + nx] = 1;
        out.push([x, y]);
        found = true;
        break;
      }
    }
    if (!found) return out;
  }
  return out;
}

// ── Ramer–Douglas–Peucker ───────────────────────────────────────────

function simplify(points: [number, number][], epsilon: number): [number, number][] {
  if (points.length < 3) return points.slice();
  return rdp(points, 0, points.length - 1, epsilon);
}

function rdp(
  pts: [number, number][],
  first: number,
  last: number,
  eps: number,
): [number, number][] {
  let maxDist = 0;
  let maxIdx = -1;
  const [ax, ay] = pts[first];
  const [bx, by] = pts[last];
  for (let i = first + 1; i < last; i++) {
    const d = perpDist(pts[i][0], pts[i][1], ax, ay, bx, by);
    if (d > maxDist) {
      maxDist = d;
      maxIdx = i;
    }
  }
  if (maxDist > eps && maxIdx !== -1) {
    const left = rdp(pts, first, maxIdx, eps);
    const right = rdp(pts, maxIdx, last, eps);
    return left.slice(0, -1).concat(right);
  }
  return [pts[first], pts[last]];
}

function perpDist(
  px: number,
  py: number,
  ax: number,
  ay: number,
  bx: number,
  by: number,
): number {
  const dx = bx - ax;
  const dy = by - ay;
  if (dx === 0 && dy === 0) {
    const ex = px - ax;
    const ey = py - ay;
    return Math.sqrt(ex * ex + ey * ey);
  }
  const num = Math.abs(dy * px - dx * py + bx * ay - by * ax);
  return num / Math.sqrt(dx * dx + dy * dy);
}

// ── Transform to scene space ────────────────────────────────────────

function toSceneSpace(
  contours: [number, number][][],
  imgW: number,
  imgH: number,
  targetWidth: number,
): [number, number, number][][] {
  if (contours.length === 0) return [];

  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const c of contours) {
    for (const [x, y] of c) {
      if (x < minX) minX = x;
      if (y < minY) minY = y;
      if (x > maxX) maxX = x;
      if (y > maxY) maxY = y;
    }
  }
  // Fallback bounds if somehow everything was a single point.
  if (!isFinite(minX)) {
    minX = 0;
    maxX = imgW;
    minY = 0;
    maxY = imgH;
  }

  const width = maxX - minX || 1;
  const height = maxY - minY || 1;
  const scale = targetWidth / Math.max(width, height);
  const cx = (minX + maxX) / 2;
  const cy = (minY + maxY) / 2;

  return contours.map((c) =>
    c.map<[number, number, number]>(([px, py]) => [
      (px - cx) * scale,
      -(py - cy) * scale, // flip Y: canvas is top-down, scene is bottom-up
      0,
    ]),
  );
}

// ── Preview renderers ───────────────────────────────────────────────

function renderTracePreview(
  source: HTMLCanvasElement,
  contours: Array<[number, number][]>,
): string {
  const out = document.createElement("canvas");
  out.width = source.width;
  out.height = source.height;
  const ctx = out.getContext("2d")!;
  ctx.drawImage(source, 0, 0);
  ctx.globalAlpha = 0.45;
  ctx.fillStyle = "#000";
  ctx.fillRect(0, 0, out.width, out.height);
  ctx.globalAlpha = 1;

  ctx.strokeStyle = "#2affea";
  ctx.lineWidth = Math.max(1, Math.round(source.width / 300));
  ctx.shadowColor = "#2affea";
  ctx.shadowBlur = 6;
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  for (const c of contours) {
    if (c.length < 2) continue;
    ctx.beginPath();
    ctx.moveTo(c[0][0], c[0][1]);
    for (let i = 1; i < c.length; i++) ctx.lineTo(c[i][0], c[i][1]);
    ctx.stroke();
  }
  return out.toDataURL("image/png");
}

function renderMask(mask: Uint8Array, w: number, h: number): string {
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d")!;
  const img = ctx.createImageData(w, h);
  for (let i = 0; i < mask.length; i++) {
    const v = mask[i] ? 255 : 0;
    const j = i * 4;
    img.data[j] = v;
    img.data[j + 1] = v;
    img.data[j + 2] = v;
    img.data[j + 3] = 255;
  }
  ctx.putImageData(img, 0, 0);
  return canvas.toDataURL("image/png");
}

/**
 * Renders the traced contours on a dark background, stroked + glowing in the
 * given color. Used for the dialog's "Neon" preview mode so the user can see
 * how a color choice will actually read before importing.
 */
export function renderNeonPreview(bundle: RenderBundle, color: string): string {
  const { width: w, height: h, pixelContours } = bundle;
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d")!;
  ctx.fillStyle = "#07070b";
  ctx.fillRect(0, 0, w, h);

  ctx.strokeStyle = color;
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  ctx.shadowColor = color;

  // Three stacked passes approximate the bloom / halo of the real scene.
  const passes = [
    { width: Math.max(6, Math.round(w / 60)), blur: Math.round(w / 20), alpha: 0.25 },
    { width: Math.max(3, Math.round(w / 140)), blur: Math.round(w / 60), alpha: 0.55 },
    { width: Math.max(1.5, Math.round(w / 260)), blur: 2, alpha: 1 },
  ];
  for (const pass of passes) {
    ctx.globalAlpha = pass.alpha;
    ctx.lineWidth = pass.width;
    ctx.shadowBlur = pass.blur;
    for (const c of pixelContours) {
      if (c.length < 2) continue;
      ctx.beginPath();
      ctx.moveTo(c[0][0], c[0][1]);
      for (let i = 1; i < c.length; i++) ctx.lineTo(c[i][0], c[i][1]);
      ctx.stroke();
    }
  }
  return canvas.toDataURL("image/png");
}
